import { useEffect, useState, useRef } from 'react'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { animate, motion, useDragControls, useMotionValue } from 'framer-motion'

// Import instruction images
import bookButtonImg from '@/assets/images/booking_instruction_photos/bookButton.jpg'
import sendAvailableTimesImg from '@/assets/images/booking_instruction_photos/sendAvailableTimes.jpg'
import priceRangeImg from '@/assets/images/booking_instruction_photos/priceRange.jpg'
import estimatedTimeImg from '@/assets/images/booking_instruction_photos/estimatedTime.jpg'
import selectAvailableDatesImg from '@/assets/images/booking_instruction_photos/selectAvailableDates.jpg'
import selectDaysImg from '@/assets/images/booking_instruction_photos/selectDays.jpg'
import selectTimesImg from '@/assets/images/booking_instruction_photos/selectTimes.jpg'
import availableTimesSentImg from '@/assets/images/booking_instruction_photos/availableTimesSent.jpg'
import userBooksTimeImg from '@/assets/images/booking_instruction_photos/userBooksTime.jpg'
import bookingConfirmedImg from '@/assets/images/booking_instruction_photos/bookingConfirmed.jpg'
import bookedTagImg from '@/assets/images/booking_instruction_photos/bookedTag.jpg'
import completeBookingImg from '@/assets/images/booking_instruction_photos/completeBooking.jpg'

interface BookingInstructionsDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

interface ImageCarouselProps {
  images: { src: string; alt: string; badge?: string }[]
}

function ImageCarousel({ images }: ImageCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleScroll = () => {
    if (!containerRef.current) return
    const container = containerRef.current
    const scrollLeft = container.scrollLeft
    const containerWidth = container.offsetWidth
    const imageWidth = containerWidth * 0.65
    const gap = 12
    
    // Calculate which image is centered
    const centerPosition = scrollLeft + containerWidth / 2
    let closestIndex = 0
    let minDistance = Infinity
    
    images.forEach((_, index) => {
      // Calculate the center position of each image
      const imageCenter = (containerWidth * 0.175) + (index * (imageWidth + gap)) + (imageWidth / 2)
      const distance = Math.abs(centerPosition - imageCenter)
      
      if (distance < minDistance) {
        minDistance = distance
        closestIndex = index
      }
    })
    
    setCurrentIndex(closestIndex)
  }

  return (
    <div className="relative w-full">
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex gap-3 overflow-x-auto hide-scrollbar snap-x snap-mandatory"
        style={{ 
          scrollBehavior: 'smooth',
          scrollPaddingLeft: '17.5%' // Centers the snap points
        }}
      >
        {/* Left padding to center first image */}
        <div className="flex-shrink-0" style={{ width: '17.5%' }} />
        
        {images.map((image, index) => (
          <div
            key={index}
            className="flex-shrink-0 snap-center relative"
            style={{ width: '65%' }}
          >
            <img
              src={image.src}
              alt={image.alt}
              className="w-full h-auto rounded-[var(--card-radius)] shadow-[0_0_15px_rgba(0,0,0,0.3)]"
            />
            {image.badge && (
              <div className="absolute top-2 left-1/2 transform -translate-x-1/2 bg-background text-foreground text-xs px-3 py-1.5 rounded-md font-medium shadow-lg border border-border whitespace-nowrap">
                {image.badge}
              </div>
            )}
          </div>
        ))}
        
        {/* Right padding to center last image */}
        <div className="flex-shrink-0" style={{ width: '17.5%' }} />
      </div>
      
      {/* Pagination dots */}
      {images.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-3">
          {images.map((_, index) => (
            <div
              key={index}
              className={cn(
                "w-1.5 h-1.5 rounded-full transition-colors",
                index === currentIndex ? "bg-primary" : "bg-muted"
              )}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export function BookingInstructionsDialog({
  isOpen,
  onOpenChange
}: BookingInstructionsDialogProps) {
  const y = useMotionValue(0)
  const dragControls = useDragControls()

  // Only reset drag offset when reopening — never zero y while closing or Radix + FM fight for one frame
  // (y.set(0) on isOpen false jumps the sheet to the top before the exit animation runs).
  useEffect(() => {
    if (isOpen) y.set(0)
  }, [isOpen, y])

  const startHeaderDrag = (ev: React.PointerEvent) => {
    if (ev.button !== 0) return
    dragControls.start(ev)
  }

  const handleDragEnd = (
    _: MouseEvent | TouchEvent | PointerEvent,
    info: { offset: { y: number }; velocity: { y: number } }
  ) => {
    const py = y.get()
    const vy = info.velocity.y
    const closeDistance =
      typeof window !== 'undefined' ? Math.min(window.innerHeight * 0.22, 260) : 220
    const shouldClose = py > closeDistance || vy > 520

    if (shouldClose) {
      const off = typeof window !== 'undefined' ? window.innerHeight + 100 : 900
      void animate(y, off, {
        duration: 0.22,
        ease: [0.32, 0.72, 0, 1],
      }).then(() => {
        onOpenChange(false)
      })
    } else {
      void animate(y, 0, {
        type: 'spring',
        stiffness: 460,
        damping: 40,
      })
    }
  }

  const maxDragDown =
    typeof window !== 'undefined' ? Math.max(window.innerHeight, 960) + 80 : 1200

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        hideCloseButton
        className={cn(
          'flex max-h-none flex-col justify-end overflow-visible rounded-none border-0 bg-transparent p-0 shadow-none',
        )}
      >
        <motion.div
          style={{ y }}
          className="flex max-h-[90dvh] w-full flex-col overflow-hidden rounded-t-2xl bg-background shadow-lg md:rounded-xl"
          drag="y"
          dragControls={dragControls}
          dragListener={false}
          dragMomentum={false}
          dragElastic={0}
          dragConstraints={{ top: 0, bottom: maxDragDown }}
          onDragEnd={handleDragEnd}
        >
          <div
            role="group"
            className={cn(
              'shrink-0 cursor-grab select-none touch-none px-6 pb-3 pt-3 active:cursor-grabbing',
            )}
            aria-label="Drag down to dismiss"
            onPointerDown={startHeaderDrag}
          >
            <div className="mx-auto mb-3 h-1 w-[2.625rem] shrink-0 rounded-full bg-muted-foreground/45" aria-hidden />
            <h2 className="text-xl font-semibold text-foreground">
              How to book sessions?
            </h2>
          </div>

          {/* Scrollable Content */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pb-safe pt-0">
            <div className="space-y-8 pb-10">
              <div className="space-y-3 text-sm text-foreground/70">
                <p>
                  There is a booking tool in the chat to help you stay organised, and it&apos;s also nice for the client since they will have a confirmation of the booking inside the chat! 🫶
                </p>
              </div>

              {/* Section 1: Book a time */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground">1. Book a time:</h3>
                <p className="text-sm text-foreground/80 -mt-1">
                  Once you are ready to book a time, click the calendar icon in the top right corner of the chat.
                </p>
                <div className="flex justify-center">
                  <img
                    src={bookButtonImg}
                    alt="Book button in chat"
                    className="w-[65%] h-auto rounded-[var(--card-radius)] shadow-[0_0_15px_rgba(0,0,0,0.3)]"
                  />
                </div>
              </div>

              {/* Section 2: Send Available Times / Record Booking */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground">
                  2. Create a Booking:
                </h3>
                <p className="text-sm text-foreground/80 -mt-1">
                  You can either send your available times to the client{' '}
                  <span className="font-semibold">OR</span> record the booking if you have already agreed on it.
                </p>
                <div className="flex justify-center">
                  <img
                    src={sendAvailableTimesImg}
                    alt="Send available times dialog"
                    className="w-[65%] h-auto rounded-[var(--card-radius)] shadow-[0_0_15px_rgba(0,0,0,0.3)]"
                  />
                </div>
              </div>

              {/* Section 2.1: Price Range */}
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-foreground">2.1. Price Range:</h3>
                <p className="text-sm text-foreground/80 -mt-1">
                  Give the client a price estimate of the tattoo / session or leave empty. An empty estimate will display as "To be discussed".
                </p>
                <div className="flex justify-center">
                  <img
                    src={priceRangeImg}
                    alt="Price range input"
                    className="w-[65%] h-auto rounded-[var(--card-radius)] shadow-[0_0_15px_rgba(0,0,0,0.3)]"
                  />
                </div>
              </div>

              {/* Section 2.2: Estimated Time */}
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-foreground">2.2. Estimated Time:</h3>
                <p className="text-sm text-foreground/80 -mt-1">
                  Give an estimated time for the tattoo / session or leave unknown.
                </p>
                <div className="flex justify-center">
                  <img
                    src={estimatedTimeImg}
                    alt="Estimated time dropdown"
                    className="w-[65%] h-auto rounded-[var(--card-radius)] shadow-[0_0_15px_rgba(0,0,0,0.3)]"
                  />
                </div>
              </div>

              {/* Section 2.3: Select Available Dates - Swipeable */}
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-foreground">2.3. Select Available Dates:</h3>
                <p className="text-sm text-foreground/80 -mt-1">
                  Select the times that the client can choose from.
                </p>
                <ImageCarousel
                  images={[
                    { src: selectAvailableDatesImg, alt: 'Select available dates button' },
                    { src: selectDaysImg, alt: 'Select days calendar' },
                    { src: selectTimesImg, alt: 'Select time slots' }
                  ]}
                />
              </div>

              {/* Section 2.4: Booking the Time - Swipeable */}
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-foreground">2.4. Booking the Time:</h3>
                <p className="text-sm text-foreground/80 -mt-1">
                  When you click "Send to Client", the client can then book their time and it will show up as "Booking Confirmed" in the chat.
                </p>
                <ImageCarousel
                  images={[
                    { src: availableTimesSentImg, alt: 'Available times sent to client' },
                    { src: userBooksTimeImg, alt: 'Client selecting time', badge: "Client's View" },
                    { src: bookingConfirmedImg, alt: 'Booking confirmed' }
                  ]}
                />
              </div>

              {/* Section 2.5: Record a Booking */}
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-foreground">2.5. Record a Booking:</h3>
                <p className="text-sm text-foreground/80 -mt-1">
                  If you have already agreed on a time for the booking, you can record it by pressing the "Time Already Agreed?" button. Once you record the booking, it will show up in the chat as a "Booking Confirmed" message.
                </p>
              </div>

              {/* Section 3: Completing the Booking */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-foreground">3. Completing the Booking:</h3>
                <p className="text-sm text-foreground/80 -mt-1">
                  After the tattoo / session, click the green checkmark on the chats page to open the "Complete Booking" form.
                </p>
                <div className="flex justify-center">
                  <img
                    src={bookedTagImg}
                    alt="Booked tag with checkmark"
                    className="w-[65%] h-auto rounded-[var(--card-radius)] shadow-[0_0_15px_rgba(0,0,0,0.3)]"
                  />
                </div>
              </div>

              {/* Section 3.1: Submit the Form */}
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-foreground">3.1. Submit the Form:</h3>
                <p className="text-sm text-foreground/80 -mt-1">
                  Fill out the form with either a Complete or No Show status, final price, notes / feedback about the booking and confirm the date.
                </p>
                <div className="flex justify-center">
                  <img
                    src={completeBookingImg}
                    alt="Complete booking form"
                    className="w-[65%] h-auto rounded-[var(--card-radius)] shadow-[0_0_15px_rgba(0,0,0,0.3)]"
                  />
                </div>
              </div>

              {/* Thank you message */}
              <div className="pt-4 text-center space-y-4">
                <div className="space-y-2">
                  <p className="text-base font-semibold text-foreground">
                    Thank you! 🙏
                  </p>
                  <p className="text-sm text-foreground/80">
                    Let us know if you have any questions on Instagram or with the Live Support feature!
                  </p>
                </div>
                <Button
                  onClick={() => onOpenChange(false)}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  Got it!
                </Button>
              </div>

            </div>
          </div>
        </motion.div>
      </SheetContent>
    </Sheet>
  )
}

